// MACHINERY V2 (Stroje) — M2.9 readiness QA gallery (TEMPORARY SCAFFOLD).
//
// Rendered by MachineryV2FoundationShell under the 9-state gallery so the
// owner can eyeball the whole MIDAS Data Readiness kit on .cc under BOTH
// palettes before M2.2 / M2.4 / M2.8 consume it.
//
// DELETE AT M2.2, together with MachineryV2FoundationShell.tsx. Nothing here
// may survive into cutover.
//
// The fixtures below are DISPLAY-ONLY mocks in the shape midasReadiness.ts
// already unit-tests (SBL 500 / SBL 300 / MCG 2318i / K'MX 816 / no-data), NOT
// a data contract: real hosts build a ReadinessModel from
// `catalogModel.readiness` and derive through the SAME
// deriveReadinessPresentation call — the gallery has no private path.
//
// Copy is intentionally NOT run through i18next (developer-facing scaffold,
// deleted before the M2.12 label audit). Every string INSIDE the kit
// components still comes from i18next (en + sk).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../shared/hooks/useTranslation";
import { ReadinessModel } from "../utils/midasReadiness";
import { CapabilityBadges } from "./readiness/CapabilityBadge";
import ReadinessBanner from "./readiness/ReadinessBanner";
import ReadinessPopupV2 from "./readiness/ReadinessPopupV2";
import ReadinessProfileCardV2 from "./readiness/ReadinessProfileCardV2";
import {
  deriveReadinessPresentation,
  readinessSummaryStrings,
} from "./readiness/readinessPresentation";

interface Fixture {
  n: string;
  title: string;
  note: string;
  name: string;
  brand: string;
  connected?: boolean;
  model: ReadinessModel;
}

const FIXTURES: Fixture[] = [
  {
    n: "R1",
    title: "OEM gold banner",
    note: "OEM partner + direct docs. bannerTier() returns oem-gold, so exactly ONE gold ribbon renders. Popup gains the dark gold panel and the Contact-OEM CTA.",
    name: "MCG 2318i",
    brand: "Tajmac-ZPS",
    model: {
      isOemPartner: true,
      oemPartnerName: "TAJMAC-ZPS",
      readiness: {
        docCounts: { alarms: 1, specSheet: 2 },
        docTypes: ["spec_sheet", "alarms"],
        languages: ["cs", "en"],
        directSources: ["machine_specs_tajmac_mcg2318i_cz"],
        brandFanoutSources: ["SINUMERIK_toc_v2"],
      },
    },
  },
  {
    n: "R2",
    title: "AI-blue banner",
    note: "Direct docs + proven capability, NOT an OEM partner. bannerTier() returns ai-blue. Telemetry badge stays unlit (possible, not connected) with an explaining tooltip.",
    name: "SBL 500 CNC",
    brand: "Trens",
    model: {
      isOemPartner: false,
      readiness: {
        docCounts: { manual: 1, electrical: 1, alarms: 1 },
        docTypes: ["manual", "electrical", "alarms"],
        languages: ["sk", "cs"],
        directSources: ["sbl500_manual_toc_v2"],
        brandFanoutSources: ["SINUMERIK_toc_v2"],
        dynamicCapability: "proven",
      },
    },
  },
  {
    n: "R3",
    title: "BOTH qualify — gold wins",
    note: "OEM partner AND AI-ready (direct + proven). Only the GOLD ribbon renders; the blue one is structurally impossible because bannerTier() is a single value, not a list.",
    name: "K'MX 620",
    brand: "Manurhin",
    model: {
      isOemPartner: true,
      oemPartnerName: "TAJMAC-ZPS",
      readiness: {
        docCounts: { manual: 2 },
        languages: ["cs", "en", "de"],
        directSources: ["kmx620_manual"],
        dynamicCapability: "proven",
      },
    },
  },
  {
    n: "R4",
    title: "Connected — static, nothing clickable",
    note: "Real gateway link. Telemetry badge is lit, but BOTH badges are inert: interactive=false, so no click target and no pointer cursor. The popup (reachable from the profile card) drops the Request-integration CTA.",
    name: "SBL 500 CNC",
    brand: "Trens",
    connected: true,
    model: {
      isOemPartner: false,
      readiness: {
        docCounts: { manual: 1, electrical: 1, alarms: 1 },
        docTypes: ["manual", "electrical", "alarms"],
        languages: ["sk", "en"],
        directSources: ["sbl500_manual_toc_v2"],
        brandFanoutSources: ["SINUMERIK_toc_v2"],
        dynamicCapability: "proven",
      },
    },
  },
  {
    n: "R5",
    title: "Absence — UNLIT, never hidden",
    note: "No readiness row at all. NO banner, but both capability badges still render (unlit + red cross) and all three dots still render. Absence stays visible; nothing disappears.",
    name: "HEM 500U",
    brand: "+GF+ Mikron",
    model: { isOemPartner: false, readiness: null },
  },
  {
    n: "R6",
    title: "Fan-out only — dot, NO banner",
    note: "Alarms arrived via the SINUMERIK brand package, no direct source. The MIDAS dot lights and the AI badge ticks, but isAiReady() is false so there is no ribbon. Tooltip says 'via the control-system package'.",
    name: "SBL 300 CNC",
    brand: "Trens",
    model: {
      isOemPartner: false,
      readiness: {
        docCounts: { alarms: 1 },
        docTypes: ["alarms"],
        languages: ["cs"],
        directSources: [],
        brandFanoutSources: ["SINUMERIK_toc_v2"],
        dynamicCapability: "possible",
      },
    },
  },
  {
    n: "R7",
    title: "DR-6 fifth doc type",
    note: "Node-scoped OEM bundle only. The popup shows ONE lit 'OEM node docs' lane plus the four base lanes dashed — the opt-in lane never appears as a permanent dashed row elsewhere.",
    name: "K'MX 816 CLEVER",
    brand: "Manurhin",
    model: {
      isOemPartner: true,
      oemPartnerName: "TAJMAC-ZPS",
      readiness: {
        docCounts: { oemNodeDocs: 6 },
        docTypes: ["oem_node_docs"],
        languages: ["cs"],
        directSources: ["machine_docs_tajmac_kmx816_cz"],
        hasDirectDocs: true,
        brandFanoutSources: [],
      },
    },
  },
];

const ReadinessQaGallery: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  const [openId, setOpenId] = React.useState<string | null>(null);
  const strings = readinessSummaryStrings(translate);

  return (
    <div style={{ marginTop: 34 }}>
      <div className="eyebrow" style={{ color: "var(--mod-accent)" }}>
        M2.9 QA gallery
      </div>
      <h2
        style={{
          margin: "4px 0 0", fontSize: 22, fontWeight: 700,
          letterSpacing: "-0.01em", color: "var(--ink)",
        }}
      >
        MIDAS data readiness
      </h2>
      <p style={{ margin: "8px 0 0", maxWidth: 760, color: "var(--muted)" }}>
        Every tile derives through the SAME deriveReadinessPresentation call,
        which only forwards midasReadiness.ts — nothing here re-derives. CLICK a
        capability badge (or tap it, or focus it and press Enter) to open the
        popup; hovering shows a text-only tooltip. On R4 the machine is
        connected, so both its badges are deliberately INERT — no pointer
        cursor, no popup — while its profile card keeps View details. Check both
        palettes.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 20,
          marginTop: 22,
        }}
      >
        {FIXTURES.map((f) => {
          const p = deriveReadinessPresentation(f.model, { connected: f.connected }, strings);
          return (
            <div key={f.n} style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span
                  style={{
                    fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700,
                    color: "var(--mod-accent)", letterSpacing: "0.1em",
                  }}
                >
                  {f.n}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                  {f.title}
                </span>
                {/* QA readout: proves the ONE tier value drives the ribbon. */}
                <span
                  style={{
                    fontFamily: "var(--font-mono)", fontSize: 9.5,
                    color: "var(--faint)", letterSpacing: "0.04em",
                  }}
                >
                  banner: {p.banner || "none"}
                </span>
              </div>

              {/* Mock card: ribbon bleeds edge to edge as the FIRST child. */}
              <div
                style={{
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 10,
                  boxShadow: "var(--shadow)",
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <ReadinessBanner presentation={p} oemPartnerName={f.model.oemPartnerName} />
                <div
                  style={{
                    padding: "10px 12px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    minWidth: 0,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 12.5,
                        color: "var(--ink)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.brand} {f.name}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>
                      {f.connected
                        ? "gateway: connected · badges inert"
                        : "gateway: not connected · click a badge"}
                    </div>
                  </div>
                  <CapabilityBadges presentation={p} onOpenDetails={() => setOpenId(f.n)} />
                </div>
              </div>

              <ReadinessProfileCardV2
                presentation={p}
                oemPartnerName={f.model.oemPartnerName}
                manufacturerName={f.brand}
                onOpenDetails={() => setOpenId(f.n)}
              />

              <div style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.45 }}>{f.note}</div>

              <ReadinessPopupV2
                open={openId === f.n}
                onOpenChange={(o) => setOpenId(o ? f.n : null)}
                presentation={p}
                machineOrModelName={`${f.brand} ${f.name}`}
                oemPartnerName={f.model.oemPartnerName}
                manufacturerName={f.brand}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReadinessQaGallery;
